import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Paper, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AdvertisingLayout from '../../layouts/AdvertisingLayout';

// Import POW icon
import powIcon from '../../assets/Sales/pow.png';

// Import all POW images
import pow1 from '../../assets/Sales/POW/316693-Inferno Cheer & Dance-268612-N1501-eNewsletters.jpg';
import pow2 from '../../assets/Sales/POW/316708-Dance 22-268071-N1501-eNewsletters.jpg';
import pow3 from '../../assets/Sales/POW/AffordableDrivingSchool.png';
import pow4 from '../../assets/Sales/POW/Blue-Mountains-Tae-Kwon-Do-01.jpg';
import pow5 from '../../assets/Sales/POW/POW-Dance-Sign-On-Ad-Examp-le.gif';
import pow6 from '../../assets/Sales/POW/TAFE-Queensland-316289-Premium-2.jpg';

const powImages = [pow1, pow2, pow3, pow4, pow5, pow6];

function POW() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotateKey, setAutoRotateKey] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (isHovering) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        return (prevIndex + 1) % powImages.length;
      });
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isHovering, autoRotateKey]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex === 0 ? powImages.length - 1 : prevIndex - 1;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % powImages.length;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setAutoRotateKey(prev => prev + 1);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setAutoRotateKey(prev => prev + 1);
  };

  // Get current 3 images to display
  const getCurrentImages = () => {
    const images = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % powImages.length;
      images.push(powImages[index]);
    }
    return images;
  };

  return (
    <AdvertisingLayout>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #247AA8 0%, #1E6A92 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Need More Pow?
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            5 Week Premium Advertisement Campaign
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: 6, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Column - Text Content with Icon */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 0, backgroundColor: 'white', overflow: 'hidden' }}>
                {/* Top Section - Text Content with Icon */}
                <Box sx={{ p: 3, display: 'flex', gap: 3 }}>
                  {/* Text Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#333132' }}>
                      Our 5 week Premium Advertisement is perfect to boost your Product, Service, Sale or Special Offer via 
                      digital school newsletters. Guaranteed to be seen in positions 2 to 5 within your selected newsletter, 
                      a POW ad will get the job done for you!
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#333132' }}>
                      This style of campaign is made to work seamlessly in conjunction with your regular advertising. 
                      Our design team will work with you to create a stand out ad that gets your message out and loud!
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, fontWeight: 600, color: '#000000' }}>
                      Perfect for:
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, mb: 0, listStyleType: 'disc' }}>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Boost your enrolments or membership
                      </Typography>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Feature your upcoming sale or event
                      </Typography>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Boost your restaurant bookings
                      </Typography>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Showcase school holiday events
                      </Typography>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Promote sports club registrations
                      </Typography>
                      <Typography component="li" sx={{ mb: 0, color: '#333132', display: 'list-item' }}>
                        Feature real estate auctions and sales
                      </Typography>
                    </Box>
                  </Box>

                  {/* POW Icon - Right side of text */}
                  <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
                    <Box
                      component="img"
                      src={powIcon}
                      alt="POW Campaign"
                      sx={{
                        maxWidth: '220px',
                        width: '100%',
                        height: 'auto'
                      }}
                    />
                  </Box>
                </Box>

                {/* Bottom Section - Terms & Conditions with Grey Background */}
                <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, fontWeight: 600, color: '#000000' }}>
                    Terms & Conditions:
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0, lineHeight: 1.6, color: '#666', fontSize: '0.875rem' }}>
                    Static POW campaigns are $249 per ad per school; animated POW campaigns are $349 per ad per school. 
                    POW ads are premium sized ads only and animation can only be applied to POWs and premium sized ads. 
                    POW campaigns are not valid with top spot positioning and will be placed randomly between positions 
                    2, 3, 4 and 5 during their 5 week period. Available for current and new advertising businesses. Not 
                    available on paper newsletters. No further discounts or package prices apply. Mid-campaign artwork 
                    changes are available (subject to Austnews Advertising artwork change pricing structure); mid-campaign 
                    animation artwork changes are available and will be charged at $100 per ad per school. Not all 
                    newsletters deploy weekly, please ensure you are clear on the number of deployments during your POW 
                    5 week period; please discuss this with your advertising consultant.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Pricing Card */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#4A9FCC',
                  color: 'white',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Feature Ad Banner */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    backgroundColor: '#FF8C00',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    transform: 'rotate(-3deg)',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    boxShadow: 3,
                    letterSpacing: 0.5
                  }}
                >
                  FEATURE AD
                </Box>

                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    mb: 1.5,
                    mt: 0.5,
                    fontSize: '1.5rem'
                  }}
                >
                  POW Campaign
                </Typography>
                
                <Box sx={{ backgroundColor: 'white', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                  {/* Grey top section - Price */}
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2.5 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#4A9FCC',
                        textAlign: 'center',
                        fontSize: '2.5rem',
                        mb: 0.5
                      }}
                    >
                      $249
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        textAlign: 'center'
                      }}
                    >
                      Premium Ad 450px (w) x 498px (h)
                    </Typography>
                  </Box>

                  {/* White bottom section - Features */}
                  <Box sx={{ p: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        5 week Digital Campaign
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Free Graphic Design
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        +$100 make it an Animated POW
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    navigate('/advertising');
                    setTimeout(() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                  sx={{
                    backgroundColor: 'white',
                    color: '#4A9FCC',
                    fontWeight: 700,
                    py: 1,
                    fontSize: '1rem',
                    border: '2px solid white',
                    borderRadius: 1,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#4A9FCC',
                      color: 'white',
                      borderColor: '#4A9FCC',
                      boxShadow: 'none',
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  Book Now
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* POW Sample Ads Section */}
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Paper elevation={0} sx={{ p: 4, backgroundColor: 'white', borderRadius: 2 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              sx={{ mb: 4, fontWeight: 600, color: '#333132', textAlign: 'left' }}
            >
              POW Sample Ads
            </Typography>

            {/* Image Carousel */}
            <Box 
              sx={{ position: 'relative', mb: 4 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
            <Grid container spacing={3} justifyContent="center">
              {getCurrentImages().map((image, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper
                    elevation={3}
                    sx={{
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={`POW Ad ${idx + 1}`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Navigation Arrows */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          {/* Dot Indicators */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            {powImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleDotClick(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: currentIndex === index ? '#247AA8' : '#ccc',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  '&:hover': {
                    backgroundColor: currentIndex === index ? '#247AA8' : '#999'
                  }
                }}
              />
            ))}
          </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Copyright Australian Newsletter Services Pty Ltd 2016-2025 | <a href="/advertising/terms-and-conditions" style={{ color: '#247AA8', textDecoration: 'none' }}>Advertising Terms & Conditions</a> | All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
        </Container>
      </Box>
    </AdvertisingLayout>
  );
}

export default POW;

